<template>
  <div class="card-team-list">
    <div class="card-team-list-trigger" @click="create">
      <i class="fa-regular fa-circle-plus card-team-list-trigger-icon"></i>
      <span>Adicionar equipe</span>
    </div>

    <div v-if="teams.length === 0" class="card-team-list-empty">
      <i class="fa-regular fa-id-badge"></i>
      <span>Nenhum membro de equipe vinculado!</span>
    </div>

    <div v-else class="card-team-list-items">
      <div v-for="item in teams" :key="item.id" class="card-team-list-item">
        <img class="card-team-list-photo" :src="photoUrl(item.photo)" @click="edit(item)"/>

        <div class="card-team-list-item-main" @click="edit(item)">
          <div class="card-team-list-name">{{ item.collaboratorName }}</div>
          <div class="card-team-list-period">{{ item.fullPeriod }}</div>
          <Tooltip v-if="descriptionText(item.description)" class="card-team-list-tooltip"
                   :title="descriptionText(item.description)">
            <div class="card-team-list-description" v-html="item.description"></div>
          </Tooltip>
        </div>
        <div class="card-team-list-item-right">
          <div class="card-team-list-approval">
            <Tooltip :title="isReproved(item.statusName) ? item.rejectionReason : ''">
              <Badge v-if="isKnownStatus(item.statusName)" :title="displayStatusName(item.statusName)"
                     :type="statusType(item.statusName)" size="small"/>
            </Tooltip>
            <div v-if="isApproved(item.statusName) && item.approvedBy" class="card-team-list-approved-by">
              <i class="fa-sharp fa-solid fa-circle-check card-team-list-approved-icon"></i>
              <span>{{ item.approvedBy }}</span>
            </div>
            <div v-else-if="isReproved(item.statusName) && item.approvedBy" class="card-team-list-approved-by">
              <i class="fa-sharp fa-solid fa-circle-xmark card-team-list-reproved-icon"></i>
              <span>{{ item.approvedBy }}</span>
            </div>
          </div>
          <div v-if="item.value !== null && item.value !== undefined" class="card-team-list-value">
            {{ item.value | currency }}
          </div>
        </div>

        <div class="card-team-list-item-remove" @click="remove(item)"
             title="Remover">
          <Loading v-if="isLoading(item.id)" type="bubbles" :center="false" :width="20" :height="20"/>
          <i v-else class="fa-solid fa-trash"></i>
        </div>
      </div>
    </div>

    <ModalChild v-model="showCreateModal" title="Equipe" :width="850" body-padding="14px 16px">
      <TeamCreate :cardId="cardId" :rentId="rentId"/>
    </ModalChild>

    <ModalChild v-model="showUpdateModal" title="Equipe" :width="850" body-padding="14px 16px">
      <TeamUpdate :cardId="cardId" :rentId="rentId"/>
    </ModalChild>
  </div>
</template>

<script>
import ModalChild from "@/ui/layout/ModalChild.vue";
import Badge from "@/ui/layout/Badge.vue";
import Tooltip from "@/ui/layout/Tooltip";
import Loading from "@/ui/shared/Loading.vue";

import TeamCreate from "./TeamCreate.vue";
import TeamUpdate from "./TeamUpdate.vue";

import TeamService from "../services/team.service";

import {mapGetters, mapMutations} from "vuex";

export default {
  name: "CardTeamList",
  components: {ModalChild, Badge, Tooltip, Loading, TeamCreate, TeamUpdate},
  props: {
    cardId: {
      type: String,
      default: "",
    },
    rentId: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      teams: [],
      showCreateModal: false,
      showUpdateModal: false,
      rentStatusEvents: ["rentAccepted", "rentProcessed", "rentRejected"],
    };
  },
  mounted() {
    this.loadTeams();
  },
  computed: {
    ...mapGetters("generic", ["event", "isLoading"]),
    genericId() {
      return this.cardId || this.rentId;
    },
  },
  methods: {
    ...mapMutations("generic", ["addEvent", "addLoading", "removeLoading"]),
    async loadTeams() {
      const response = await TeamService.getAll(this.genericId);
      this.teams = response.data.content || [];
      this.$emit("count-change", this.teams.length);
    },
    create() {
      this.showCreateModal = true;
    },
    edit(item) {
      this.showUpdateModal = true;
      this.addEvent({name: "updateCardTeam", data: item});
    },
    async remove(item) {
      if (this.isLoading(item.id)) return;
      this.addLoading(item.id);
      try {
        await TeamService.delete(item.id);
        await this.loadTeams();
      } finally {
        this.removeLoading([item.id]);
      }
    },
    descriptionText(description) {
      if (!description) return "";
      return String(description)
          .replace(/<[^>]*>/g, " ")
          .replace(/&nbsp;/g, " ")
          .replace(/\s+/g, " ")
          .trim();
    },
    photoUrl(photo) {
      return `https://espaco.blob.core.windows.net/nixloc-photo-collaborator/${photo}`;
    },
    isApproved(statusName) {
      if (!statusName) return false;
      return String(statusName).toLowerCase() === "autorizada";
    },
    isReproved(statusName) {
      if (!statusName) return false;
      const s = this.normalizeStatus(statusName);
      return s === "reprovada" || s === "reprovado" || s === "nao autorizada";
    },
    normalizeStatus(statusName) {
      return String(statusName)
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase();
    },
    isKnownStatus(statusName) {
      if (!statusName) return false;
      const s = this.normalizeStatus(statusName);
      return s === "pendente" || s === "autorizada" || s === "reprovada" || s === "reprovado" || s === "nao autorizada" || s === "sem vinculo";
    },
    displayStatusName(statusName) {
      const s = this.normalizeStatus(statusName);
      if (s === "sem vinculo") return "Sem Vínculo";
      if (s === "reprovada" || s === "reprovado") return "Não Autorizada";
      return statusName;
    },
    statusType(statusName) {
      if (!statusName) return "info";
      const s = this.normalizeStatus(statusName);
      if (s === "autorizada") return "success";
      if (s === "reprovada" || s === "reprovado" || s === "nao autorizada") return "danger";
      if (s === "sem vinculo") return "default";
      return "warning";
    },
  },
  watch: {
    event: {
      handler(event) {
        if (event.name === "reloadCardTeam") {
          this.showCreateModal = false;
          this.showUpdateModal = false;
          this.loadTeams();
        }

        if (this.rentStatusEvents.includes(event.name)) {
          this.loadTeams();
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.card-team-list-trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  color: var(--text-secondary);
  font-size: 14px;
  cursor: pointer;
  transition: background 0.15s;
  margin-bottom: 8px;
}

.card-team-list-trigger:hover {
  background: var(--bg-hover);
}

.card-team-list-trigger-icon {
  font-size: 16px;
  color: var(--text-muted);
}

.card-team-list-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 32px 0;
  color: var(--text-muted);
  font-size: 14px;
  font-style: italic;
}

.card-team-list-empty i {
  font-size: 28px;
}

.card-team-list-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.card-team-list-item {
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid var(--border-subtle);
  border-radius: 12px;
  background: var(--bg-base);
  padding: 10px 12px;
  transition: background 0.15s, box-shadow 0.15s;
}

.card-team-list-item:hover {
  background: var(--bg-page);
  box-shadow: var(--shadow-sm);
}

.card-team-list-photo {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid var(--border-color);
  object-fit: cover;
  flex-shrink: 0;
  cursor: pointer;
}

.card-team-list-item-main {
  flex: 1;
  min-width: 0;
  cursor: pointer;
}

.card-team-list-name {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-brand);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: opacity 0.15s;
}

.card-team-list-item-main:hover .card-team-list-name {
  text-decoration: underline;
  opacity: 0.8;
}

.card-team-list-period {
  font-size: 12px;
  color: var(--text-muted);
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-team-list-tooltip {
  display: block;
  min-width: 0;
}

.card-team-list-description {
  font-size: 12px;
  color: var(--text-secondary);
  font-style: italic;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-team-list-description ::v-deep p,
.card-team-list-description ::v-deep div,
.card-team-list-description ::v-deep ul,
.card-team-list-description ::v-deep ol {
  display: inline;
  margin: 0;
  padding: 0;
}

.card-team-list-description ::v-deep br {
  display: none;
}

.card-team-list-description ::v-deep img {
  display: none;
}

.card-team-list-item-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  flex-shrink: 0;
}

.card-team-list-approval {
  display: flex;
  align-items: center;
  gap: 6px;
}

.card-team-list-approved-by {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--text-secondary);
}

.card-team-list-approved-icon {
  color: var(--color-success);
}

.card-team-list-reproved-icon {
  color: var(--color-danger);
}

.card-team-list-value {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-danger);
  white-space: nowrap;
}

.card-team-list-item-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: var(--color-danger);
  cursor: pointer;
  flex-shrink: 0;
  transition: color 0.15s, opacity 0.15s;
  font-size: 14px;
  margin-left: 10px;
}

.card-team-list-item-remove:hover {
  opacity: 0.7;
}
</style>
